import { Injectable, Injector } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BookingReportState } from './booking-report.state';
import { SpinnerOverlayService } from '@deltager/organizers/admin/shared/spinner-overlay/spinner-overlay.service';
import { BookingReportApiService } from '../booking-report.api.service';
import {
  BookingReportActionTypes,
  BookingReportLoadCalendarItemsRequestAction,
  BookingReportLoadCalendarItemsSuccessAction,
  BookingReportLoadTableItemsRequestAction,
  BookingReportLoadTableItemsSuccessAction,
} from './booking-report.actions';
import { catchError, finalize, mergeMap, switchMap, take, throwError } from 'rxjs';
import { selectBookingReportCalendarFilters, selectBookingReportTableFilters } from './booking-report.selectors';
import { BookingReportCalendarItem } from '../booking-report-calendar/booking-report-calendar.models';
import { BookingReportTableItem } from '../booking-report-table/booking-report-table.models';
import { PagedList } from '@deltager/organizers/admin/shared/models/paged-list.model';
import { isNullOrUndefined } from '@deltager/organizers/admin/shared/utils/object.utils';
import { DayDate } from '@deltager/organizers/admin/core/date-format/dayDate';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BookingReportEffects {
  private readonly DefaultVirtualKey = '-1';

  constructor(
    private actions$: Actions,
    private store: Store<{ bookingReport: BookingReportState }>,
    private spinner: SpinnerOverlayService,
    private bookingReportApiService: BookingReportApiService,
    private injector: Injector,
  ) {}

  private get translateService(): TranslateService {
    return this.injector.get(TranslateService);
  }

  loadCalendarItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingReportActionTypes.LoadCalendarItemsRequest),
      mergeMap((action: BookingReportLoadCalendarItemsRequestAction) => {
        this.spinner.show();

        return this.store.select(selectBookingReportCalendarFilters).pipe(
          filter(x => !!x),
          take(1),
          switchMap(filters => {
            return this.bookingReportApiService.loadCalendarItems(filters);
          }),
          switchMap((items: BookingReportCalendarItem[]) => {
            items.forEach(item => {
              item.venue =
                item.venue === this.DefaultVirtualKey
                  ? this.translateService.instant('repeating.virtual_event')
                  : item.venue;

              if (item.session) {
                item.session.isFullDay = item.session.timeSlotFrom == item.session.timeSlotTo;
                item.session.timeSlotFrom = DayDate.toUtc(item.session.timeSlotFrom);
                item.session.timeSlotTo = DayDate.toUtc(item.session.timeSlotTo);
              }
            });

            this.spinner.hide();

            return [new BookingReportLoadCalendarItemsSuccessAction(items)];
          }),
          catchError(err => {
            return throwError(() => err);
          }),
          finalize(() => {
            this.spinner.hide();
          }),
        );
      }),
    );
  });

  loadTableItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BookingReportActionTypes.LoadTableItemsRequest),
      mergeMap((action: BookingReportLoadTableItemsRequestAction) => {
        this.spinner.show();

        const pageNumber = action.payload ? action.payload.pageNumber : null;

        return this.store.select(selectBookingReportTableFilters).pipe(
          take(1),
          switchMap(filters => {
            const adjustedFilters = isNullOrUndefined(pageNumber) ? filters : { ...filters, pageNumber: pageNumber };

            return this.bookingReportApiService.loadTableItems(adjustedFilters);
          }),
          switchMap((pagedList: PagedList<BookingReportTableItem>) => {
            pagedList.items.forEach(item => {
              item.venue =
                item.venue === this.DefaultVirtualKey
                  ? this.translateService.instant('repeating.virtual_event')
                  : item.venue;

              if (item.session) {
                item.session.timeSlotFrom = DayDate.toUtc(item.session.timeSlotFrom);
                item.session.timeSlotTo = DayDate.toUtc(item.session.timeSlotTo);
              }
            });

            this.spinner.hide();

            return [new BookingReportLoadTableItemsSuccessAction({ items: pagedList, pageNumber: pageNumber })];
          }),
          catchError(err => {
            return throwError(() => err);
          }),
          finalize(() => this.spinner.hide()),
        );
      }),
    );
  });
}
